import React, { useState, useEffect } from "react";
import { X, Save, Loader2, Phone, ShieldCheck, Key, FileText, Globe } from "lucide-react";
import integrationapi from "../../../services/integrationapi";
import { showError, showSuccess } from "../../../utils/toast";
import { useClientLevel } from "../../../hooks/useClientLevel";
import SingleSelectLocalSearchDropdown from "../../../components/SingleSelectLocalSearchDropdown";

export const WHATSAPP_LANGUAGES = [
    { label: "English (US) [en_US]", value: "en_US" },
    { label: "English [en]", value: "en" },
    { label: "English (UK) [en_GB]", value: "en_GB" },
    { label: "Spanish [es]", value: "es" },
    { label: "Spanish (LATAM) [es_LA]", value: "es_LA" },
    { label: "Spanish (Spain) [es_ES]", value: "es_ES" },
    { label: "Hindi [hi]", value: "hi" },
    { label: "Portuguese (BR) [pt_BR]", value: "pt_BR" },
    { label: "Portuguese (PT) [pt_PT]", value: "pt_PT" },
    { label: "Arabic [ar]", value: "ar" },
    { label: "French [fr]", value: "fr" },
    { label: "German [de]", value: "de" },
    { label: "Italian [it]", value: "it" },
    { label: "Indonesian [id]", value: "id" },
    { label: "Japanese [ja]", value: "ja" },
    { label: "Korean [ko]", value: "ko" },
    { label: "Chinese (Simplified) [zh_CN]", value: "zh_CN" },
    { label: "Chinese (Traditional) [zh_TW]", value: "zh_TW" },
    { label: "Russian [ru]", value: "ru" },
    { label: "Turkish [tr]", value: "tr" },
    { label: "Dutch [nl]", value: "nl" },
    { label: "Vietnamese [vi]", value: "vi" },
    { label: "Thai [th]", value: "th" },
    { label: "Hebrew [he]", value: "he" },
    { label: "Bengali [bn]", value: "bn" },
    { label: "Gujarati [gu]", value: "gu" },
    { label: "Kannada [kn]", value: "kn" },
    { label: "Malayalam [ml]", value: "ml" },
    { label: "Marathi [mr]", value: "mr" },
    { label: "Punjabi [pa]", value: "pa" },
    { label: "Tamil [ta]", value: "ta" },
    { label: "Telugu [te]", value: "te" },
    { label: "Urdu [ur]", value: "ur" },
    { label: "Afrikaans [af]", value: "af" },
    { label: "Albanian [sq]", value: "sq" },
    { label: "Bulgarian [bg]", value: "bg" },
    { label: "Catalan [ca]", value: "ca" },
    { label: "Croatian [hr]", value: "hr" },
    { label: "Czech [cs]", value: "cs" },
    { label: "Danish [da]", value: "da" },
    { label: "Greek [el]", value: "el" },
    { label: "Finnish [fi]", value: "fi" },
    { label: "Hungarian [hu]", value: "hu" },
    { label: "Malay [ms]", value: "ms" },
    { label: "Norwegian [nb]", value: "nb" },
    { label: "Polish [pl]", value: "pl" },
    { label: "Romanian [ro]", value: "ro" },
    { label: "Serbian [sr]", value: "sr" },
    { label: "Slovak [sk]", value: "sk" },
    { label: "Swedish [sv]", value: "sv" },
    { label: "Ukrainian [uk]", value: "uk" }
];

interface WhatsappIntegrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    integrationData?: any;
}

const WhatsappIntegrationModal: React.FC<WhatsappIntegrationModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    integrationData
}) => {
    const level = useClientLevel();
    const isEditing = !!integrationData;

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        phone_number_id: '',
        waba_id: '',
        access_token: '',
        default_template: '',
        template_language: 'en'
    });

    useEffect(() => {
        if (isOpen) {
            if (isEditing && integrationData?.integration_data) {
                const data = typeof integrationData.integration_data === 'string'
                    ? JSON.parse(integrationData.integration_data)
                    : integrationData.integration_data;
                setFormData({
                    phone_number_id: data.phone_number_id || integrationData.phone_number_id || '',
                    waba_id: data.waba_id || integrationData.waba_id || '',
                    access_token: data.access_token || integrationData.access_token || '',
                    default_template: data.default_template || integrationData.default_template || '',
                    template_language: data.template_language || integrationData.template_language || 'en'
                });
            } else if (isEditing && integrationData) {
                setFormData({
                    phone_number_id: integrationData.phone_number_id || '',
                    waba_id: integrationData.waba_id || '',
                    access_token: integrationData.access_token || '',
                    default_template: integrationData.default_template || '',
                    template_language: integrationData.template_language || 'en'
                });
            } else {
                setFormData({
                    phone_number_id: '',
                    waba_id: '',
                    access_token: '',
                    default_template: '',
                    template_language: 'en'
                });
            }
            setIsLoading(false);
        }
    }, [isOpen, isEditing, integrationData]);

    if (!isOpen) return null;

    // const handleBackdropClick = (e: React.MouseEvent) => {
    //     if (e.target === e.currentTarget) {
    //         onClose();
    //     }
    // };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.phone_number_id.trim() || !formData.waba_id.trim() || !formData.access_token.trim() || !formData.default_template.trim() || !formData.template_language.trim()) {
            showError("All fields are required");
            return;
        }

        setIsLoading(true);
        try {
            const payload: any = {
                phone_number_id: formData.phone_number_id.trim(),
                waba_id: formData.waba_id.trim(),
                access_token: formData.access_token.trim(),
                default_template: formData.default_template.trim(),
                template_language: formData.template_language.trim()
            };

            const config: any = {};
            if (level && level.name !== 'global' && level.id) {
                config.params = { client_id: level.id };
            }

            const response = await integrationapi.post("integrations/whatsapp", payload, config);

            if (response.data?.status || response.data?.success) {
                showSuccess(`WhatsApp integration ${isEditing ? 'updated' : 'installed'} successfully`);
                onSuccess();
                onClose();
            } else {
                showError(response.data?.message || "Operation failed");
            }
        } catch (error: any) {
            console.error("WhatsApp install error:", error);
            showError(error.response?.data?.message || error.message || "Failed to install WhatsApp integration");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
        >
            <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-700/50 rounded-2xl w-full max-w-xl shadow-2xl animate-in zoom-in duration-300 relative flex flex-col max-h-[90vh]">
                <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between shrink-0 bg-gray-50/50 dark:bg-[#111827]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                            <img src="/whatsapp.png" alt="WhatsApp" className="w-6 h-6 object-contain" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{isEditing ? 'Update WhatsApp Integration' : 'Install WhatsApp Integration'}</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Configure Meta WhatsApp Business API credentials</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors cursor-pointer">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                    {integrationData?.editable === false && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl text-blue-700 dark:text-blue-400 text-xs flex items-center gap-2.5">
                            <Globe className="w-4 h-4 shrink-0" />
                            <span>This global integration is managed centrally. Settings are read-only.</span>
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Phone Number ID */}
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-1">Phone Number ID *</label>
                            <div className="relative group">
                                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 group-focus-within:text-emerald-500 transition-colors" />
                                <input
                                    type="text"
                                    disabled={integrationData?.editable === false}
                                    value={formData.phone_number_id}
                                    onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-emerald-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                    placeholder="e.g. 100609346252980"
                                    required
                                />
                            </div>
                        </div>

                        {/* WABA ID */}
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-1">WABA ID *</label>
                            <div className="relative group">
                                <ShieldCheck className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 group-focus-within:text-emerald-500 transition-colors" />
                                <input
                                    type="text"
                                    disabled={integrationData?.editable === false}
                                    value={formData.waba_id}
                                    onChange={(e) => setFormData({ ...formData, waba_id: e.target.value })}
                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-emerald-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                    placeholder="e.g. 109283746501928"
                                    required
                                />
                            </div>
                        </div>

                        {/* Access Token */}
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-1">Access Token *</label>
                            <div className="relative group">
                                <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 group-focus-within:text-emerald-500 transition-colors" />
                                <input
                                    type="password"
                                    disabled={integrationData?.editable === false}
                                    value={formData.access_token}
                                    onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-emerald-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed font-mono text-xs"
                                    placeholder="EAAG..."
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Default Template */}
                            <div>
                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-1">Default Template *</label>
                                <div className="relative group">
                                    <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500 group-focus-within:text-emerald-500 transition-colors" />
                                    <input
                                        type="text"
                                        disabled={integrationData?.editable === false}
                                        value={formData.default_template}
                                        onChange={(e) => setFormData({ ...formData, default_template: e.target.value })}
                                        className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-emerald-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                        placeholder="e.g. hello_world"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Template Language */}
                            <div>
                                <SingleSelectLocalSearchDropdown
                                    label="Template Language *"
                                    value={formData.template_language}
                                    options={WHATSAPP_LANGUAGES}
                                    onChange={(val) => setFormData({ ...formData, template_language: val || '' })}
                                    disabled={integrationData?.editable === false}
                                    placeholder="Select Language"
                                    icon={Globe}
                                    searchFields={['label', 'value']}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white text-sm font-medium transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                        {integrationData?.editable !== false && (
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-all shadow-lg flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>{isEditing ? 'Save Changes' : 'Install Integration'}</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WhatsappIntegrationModal;
